import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAttributeDefinitions,
  createAttributeDefinition,
  updateAttributeDefinition,
  deactivateAttributeDefinition,
} from "../services/contactAttributes.service";
import { useToast } from "../components/ui/Toast";
import { useAuth } from "../contexts/AuthContext";
import { normalizeRole } from "../utils/roleUtils";
import type {
  AttributeDefinition,
  AttributeDataType,
  AttributeApplicableTo,
  CreateAttributeDefinitionDTO,
  UpdateAttributeDefinitionDTO,
} from "../types/contactAttribute.types";

const DATA_TYPE_LABELS: Record<AttributeDataType, string> = {
  text: "Text",
  number: "Number",
  boolean: "Yes / No",
  date: "Date",
  select: "Dropdown",
};

const APPLICABLE_TO_LABELS: Record<AttributeApplicableTo, string> = {
  all: "All Contacts",
  contact_type: "Specific Contact Type(s)",
  member_account: "Member Account (Primary)",
  member_individual: "Member Individual",
};

const CONTACT_TYPE_OPTIONS = [
  "Member",
  "Organization Contact",
  "Lead",
  "Prospect",
  "Partner",
];

interface FormState {
  name: string;
  description: string;
  data_type: AttributeDataType;
  is_multi_valued: boolean;
  select_options_raw: string;
  applicable_to: AttributeApplicableTo;
  applicable_contact_types: string[];
  display_order: string;
}

const DEFAULT_FORM: FormState = {
  name: "",
  description: "",
  data_type: "text",
  is_multi_valued: false,
  select_options_raw: "",
  applicable_to: "all",
  applicable_contact_types: [],
  display_order: "0",
};

function defToForm(def: AttributeDefinition): FormState {
  return {
    name: def.name,
    description: def.description ?? "",
    data_type: def.data_type,
    is_multi_valued: def.is_multi_valued,
    select_options_raw: def.select_options?.join("\n") ?? "",
    applicable_to: def.applicable_to,
    applicable_contact_types: def.applicable_contact_types ?? [],
    display_order: String(def.display_order),
  };
}

function parseForm(form: FormState): CreateAttributeDefinitionDTO {
  const select_options =
    form.data_type === "select"
      ? form.select_options_raw
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined;
  return {
    name: form.name.trim(),
    description: form.description.trim() || undefined,
    data_type: form.data_type,
    is_multi_valued: form.is_multi_valued,
    select_options,
    applicable_to: form.applicable_to,
    applicable_contact_types:
      form.applicable_to === "contact_type"
        ? form.applicable_contact_types
        : undefined,
    display_order: parseInt(form.display_order) || 0,
  };
}

interface AttributeFormProps {
  initial: FormState;
  onSave: (data: CreateAttributeDefinitionDTO) => void;
  onCancel: () => void;
  isSaving: boolean;
}

const AttributeForm: React.FC<AttributeFormProps> = ({
  initial,
  onSave,
  onCancel,
  isSaving,
}) => {
  const [form, setForm] = useState<FormState>(initial);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      return;
    }
    onSave(parseForm(form));
  };

  const toggleContactType = (t: string) => {
    set(
      "applicable_contact_types",
      form.applicable_contact_types.includes(t)
        ? form.applicable_contact_types.filter((x) => x !== t)
        : [...form.applicable_contact_types, t],
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          required
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="e.g. Preferred Language"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Optional hint for staff"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data Type
          </label>
          <select
            value={form.data_type}
            onChange={(e) =>
              set("data_type", e.target.value as AttributeDataType)
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {(
              Object.entries(DATA_TYPE_LABELS) as [AttributeDataType, string][]
            ).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display Order
          </label>
          <input
            type="number"
            value={form.display_order}
            onChange={(e) => set("display_order", e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            min={0}
          />
        </div>
      </div>

      {form.data_type === "select" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Options (one per line)
          </label>
          <textarea
            value={form.select_options_raw}
            onChange={(e) => set("select_options_raw", e.target.value)}
            rows={4}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder={"Option A\nOption B\nOption C"}
          />
        </div>
      )}

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="is_multi_valued"
          checked={form.is_multi_valued}
          onChange={(e) => set("is_multi_valued", e.target.checked)}
          className="h-4 w-4 text-primary-600 border-gray-300 rounded"
        />
        <label htmlFor="is_multi_valued" className="text-sm text-gray-700">
          Allow multiple values
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Show For
        </label>
        <select
          value={form.applicable_to}
          onChange={(e) =>
            set("applicable_to", e.target.value as AttributeApplicableTo)
          }
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {(
            Object.entries(APPLICABLE_TO_LABELS) as [
              AttributeApplicableTo,
              string,
            ][]
          ).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>

      {form.applicable_to === "contact_type" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contact Types
          </label>
          <div className="flex flex-wrap gap-2">
            {CONTACT_TYPE_OPTIONS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => toggleContactType(t)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  form.applicable_contact_types.includes(t)
                    ? "bg-primary-600 text-white border-primary-600"
                    : "bg-white text-gray-700 border-gray-300 hover:border-primary-400"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving || !form.name.trim()}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
};

const ContactAttributesAdmin: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const isAdmin = ["superadmin", "admin"].includes(normalizeRole(user?.role));

  const { data: defs = [], isLoading } = useQuery({
    queryKey: ["attr-defs"],
    queryFn: getAttributeDefinitions,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AttributeDefinition | null>(
    null,
  );

  const createMutation = useMutation({
    mutationFn: createAttributeDefinition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attr-defs"] });
      setModalOpen(false);
      showToast("success", "Attribute created");
    },
    onError: () => showToast("error", "Failed to create attribute"),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateAttributeDefinitionDTO;
    }) => updateAttributeDefinition(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attr-defs"] });
      setEditTarget(null);
      showToast("success", "Attribute updated");
    },
    onError: () => showToast("error", "Failed to update attribute"),
  });

  const deactivateMutation = useMutation({
    mutationFn: deactivateAttributeDefinition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attr-defs"] });
      showToast("success", "Attribute deactivated");
    },
    onError: () => showToast("error", "Failed to deactivate attribute"),
  });

  const handleSaveNew = (data: CreateAttributeDefinitionDTO) => {
    createMutation.mutate(data);
  };

  const handleSaveEdit = (data: CreateAttributeDefinitionDTO) => {
    if (!editTarget) {
      return;
    }
    updateMutation.mutate({ id: editTarget.id, updates: data });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="mb-1">
            <Link
              to="/contacts"
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              ← Back to Contacts
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Custom Attributes
          </h1>
          <p className="text-gray-600">
            Manage attributes that appear on contact records.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setModalOpen(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 flex items-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            New Attribute
          </button>
        )}
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : defs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No custom attributes defined yet.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Multi
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Applies To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                {isAdmin && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {defs.map((def) => (
                <tr
                  key={def.id}
                  className={def.is_active ? "" : "opacity-50 bg-gray-50"}
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {def.name}
                    </div>
                    {def.description && (
                      <div className="text-xs text-gray-500">
                        {def.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {DATA_TYPE_LABELS[def.data_type]}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {def.is_multi_valued ? "Yes" : "No"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {APPLICABLE_TO_LABELS[def.applicable_to]}
                    {def.applicable_contact_types?.length ? (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {def.applicable_contact_types.join(", ")}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {def.display_order}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        def.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {def.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4 text-right space-x-3">
                      <button
                        onClick={() => setEditTarget(def)}
                        className="text-sm text-primary-600 hover:text-primary-800"
                      >
                        Edit
                      </button>
                      {def.is_active && (
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                `Deactivate "${def.name}"? Existing values are preserved.`,
                              )
                            ) {
                              deactivateMutation.mutate(def.id);
                            }
                          }}
                          disabled={deactivateMutation.isPending}
                          className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                New Attribute
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <AttributeForm
                initial={DEFAULT_FORM}
                onSave={handleSaveNew}
                onCancel={() => setModalOpen(false)}
                isSaving={createMutation.isPending}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                Edit Attribute
              </h2>
              <button
                onClick={() => setEditTarget(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <AttributeForm
                initial={defToForm(editTarget)}
                onSave={handleSaveEdit}
                onCancel={() => setEditTarget(null)}
                isSaving={updateMutation.isPending}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactAttributesAdmin;
